import useSWR from "swr";
import { AtSign } from "lucide-react";
import { bindReference, referenceBindings } from "@/lib/mentions";
import {
  productionAssetsPath,
  swrFetcher,
  type ProductionAssetInfo,
} from "@/lib/api";

interface Props {
  params: Record<string, unknown>;
  onChange: (name: string, value: unknown) => void;
}

/**
 * Bind an accessible Element by ID. The server resolves and freezes its
 * canonical images at quote time; the displayed slug is only an alias.
 *
 * Hidden entirely when there are no active elements to mention.
 */
export function MentionPicker({ params, onChange }: Props) {
  // The endpoint enforces owner isolation and the selected admin team.
  const { data } = useSWR<ProductionAssetInfo[]>(
    productionAssetsPath(undefined, undefined, false),
    swrFetcher
  );

  const assets = (data ?? []).filter((a) => !a.archived);
  if (assets.length === 0) return null;

  const insert = (asset: ProductionAssetInfo) => {
    const binding = bindReference(params, { token: `@${asset.slug}`, kind: "element", id: asset.id, updated_at: asset.updated_at });
    const current = typeof params.prompt === "string" ? params.prompt : "";
    const needsSpace = current.length > 0 && !/\s$/.test(current);
    onChange("reference_bindings", [...referenceBindings(params).filter((item) => item.token !== binding.token), binding]);
    onChange("prompt", current + (needsSpace ? " " : "") + binding.token);
  };

  return (
    <div className="rounded-xl border border-studio-border bg-studio-card/50 p-3.5">
      <label className="mb-2 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-studio-muted">
        <AtSign className="h-3 w-3" />
        Mention elements
      </label>
      <div className="flex flex-wrap gap-1.5">
        {assets.map((a) => (
          <button
            key={a.id}
            type="button"
            onClick={() => insert(a)}
            title={a.summary || a.name}
            className="min-h-11 rounded-md border border-studio-border bg-studio-panel px-2 py-1 font-mono text-[11px] text-studio-muted transition-colors hover:border-studio-accent/50 hover:text-studio-ink sm:min-h-10"
          >
            @{a.slug}
          </button>
        ))}
      </div>
      <p className="mt-2.5 text-[11px] leading-relaxed text-studio-faint">
        Mentions inject each element's canonical reference images into the prompt.
      </p>
    </div>
  );
}
